import React, { useState, useEffect, useContext } from 'react';
import { List, Card, Descriptions, Tag, Typography, Space, Skeleton, Collapse, Popconfirm, Button, message } from 'antd';
import { LinkOutlined, DeleteOutlined, PlusOutlined, CheckOutlined } from '@ant-design/icons';
import { Formik } from 'formik';
import { Form, Input, Select as FormikSelect, DatePicker } from 'formik-antd';
import moment from 'moment-timezone';
import api from '../api';
import { useTranslation } from 'react-i18next';
import { AssetModalContext } from './AssetModal';

const PublishedImages = ({ asset }) => {
    const { t } = useTranslation();
    const [publishedImages, setPublishedImages] = useState([]);
    const [loading, setLoading] = useState(true);

    const loadPublishedImages = () => {
        if (!asset?.id) return;

        setLoading(true);
        api(`/api/assets/${asset.id}/published_images`)
            .then(res => {
                setPublishedImages(res.data);
                setLoading(false);
            })
            .catch(err => {
                console.error('Failed to load published images:', err);
                setLoading(false);
            });
    };

    useEffect(() => {
        loadPublishedImages();
    }, [asset?.id]);

    const handleDelete = (publishedImageId) => {
        api.delete(`/api/published_images/${publishedImageId}`)
            .then(() => {
                setPublishedImages(prev => prev.filter(pi => pi.id !== publishedImageId));
                message.success(t('publication-deleted', 'Publication record deleted.'));
            })
            .catch(err => {
                console.error('Failed to delete published image:', err);
                message.error(t('publication-delete-error', 'Failed to delete publication record.'));
            });
    };

    const handleCreate = (values, actions) => {
        actions.setSubmitting(true);

        const params = {
            published_asset: {
                publisher_uid: values.publisher_uid,
                publisher_name: values.publisher_name,
                publication_name: values.publication_name,
                edition: values.edition,
                article_uid: values.article_uid,
                article_name: values.article_name,
                headline: values.headline,
                subhead: values.subhead,
                byline: values.byline,
                abstract: values.abstract,
                publication_urls: values.publication_urls,
            },
            published_images: [
                {
                    asset_guid: asset.guid,
                    filename: values.filename,
                    url: values.url,
                    cdn_link: values.cdn_link,
                    usage_type: values.usage_type,
                    credit_line: values.credit_line,
                    caption: values.caption,
                    alt_text: values.alt_text,
                    restrictions: values.restrictions,
                }
            ]
        };

        if (values.publication_date) {
            params.published_asset.publication_date = values.publication_date.toISOString();
        }

        api.post('/api/published_assets', params)
            .then(() => {
                message.success(t('publication-created', 'Publication record created.'));
                actions.setSubmitting(false);
                actions.resetForm();
                loadPublishedImages();
            })
            .catch(err => {
                console.error('Failed to create published asset:', err);
                message.error(t('publication-create-error', 'Failed to create publication record.'));
                actions.setSubmitting(false);
            });
    };

    if (loading) {
        return <Skeleton active />;
    }

    return (
        <>
            {publishedImages.length === 0 && (
                <p style={{ textAlign: 'center', color: '#999', padding: '20px 20px 0' }}>
                    {t('no-published-articles', 'This asset has not been published yet.')}
                </p>
            )}

            {publishedImages.length > 0 && (
                <List
                    dataSource={publishedImages}
                    renderItem={pi => (
                        <List.Item>
                            <Card
                                title={
                                    <Space direction="vertical" size="small" style={{ width: '100%' }}>
                                        <Typography.Title level={5} style={{ margin: 0 }}>
                                            {pi.published_asset.headline || pi.published_asset.article_name}
                                        </Typography.Title>
                                        {pi.published_asset.subhead && (
                                            <Typography.Text type="secondary">
                                                {pi.published_asset.subhead}
                                            </Typography.Text>
                                        )}
                                    </Space>
                                }
                                extra={asset.editable && (
                                    <Popconfirm
                                        title={t('confirm-delete-publication', 'Delete this publication record?')}
                                        onConfirm={() => handleDelete(pi.id)}
                                        okText={t('yes', 'Yes')}
                                        cancelText={t('no', 'No')}
                                    >
                                        <Button type="text" danger icon={<DeleteOutlined />} size="small" />
                                    </Popconfirm>
                                )}
                                size="small"
                                style={{ width: '100%', marginBottom: '1em' }}
                            >
                                <Descriptions size="small" column={1}>
                                    <Descriptions.Item label={t('article-uid', 'Article UID')}>
                                        <Typography.Text code copyable>
                                            {pi.published_asset.article_uid}
                                        </Typography.Text>
                                    </Descriptions.Item>
                                    {pi.published_asset.article_name && (
                                        <Descriptions.Item label={t('article-name', 'Article Name')}>
                                            {pi.published_asset.article_name}
                                        </Descriptions.Item>
                                    )}
                                    <Descriptions.Item label={t('publisher', 'Publisher')}>
                                        {pi.published_asset.publisher_name}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={t('publication', 'Publication')}>
                                        {pi.published_asset.publication_name}
                                        {pi.published_asset.edition && ` (${pi.published_asset.edition})`}
                                    </Descriptions.Item>
                                    {pi.published_asset.byline && (
                                        <Descriptions.Item label={t('article-byline', 'Article Author')}>
                                            {pi.published_asset.byline}
                                        </Descriptions.Item>
                                    )}
                                    {pi.published_asset.publication_date && (
                                        <Descriptions.Item label={t('publication-date', 'Publication Date')}>
                                            {moment(pi.published_asset.publication_date).format('MMMM D, YYYY [at] h:mm A')}
                                        </Descriptions.Item>
                                    )}
                                    {pi.usage_type && (
                                        <Descriptions.Item label={t('usage-type', 'Usage Type')}>
                                            <Tag>{pi.usage_type.replace('_', ' ').toProperCase()}</Tag>
                                        </Descriptions.Item>
                                    )}
                                    {pi.published_asset.publication_urls && pi.published_asset.publication_urls.length > 0 && (
                                        <Descriptions.Item label={t('article-url', 'Article URL')}>
                                            {pi.published_asset.publication_urls.map((url, idx) => (
                                                <div key={idx}>
                                                    <a href={url} target="_blank" rel="noopener noreferrer">
                                                        <LinkOutlined /> {url}
                                                    </a>
                                                </div>
                                            ))}
                                        </Descriptions.Item>
                                    )}
                                    {pi.caption && (
                                        <Descriptions.Item label={t('caption', 'Caption (Article-Specific)')}>
                                            {pi.caption}
                                        </Descriptions.Item>
                                    )}
                                    {pi.alt_text && (
                                        <Descriptions.Item label={t('alt-text', 'Alt Text (Article-Specific)')}>
                                            {pi.alt_text}
                                        </Descriptions.Item>
                                    )}
                                    {pi.metadata?.title && (
                                        <Descriptions.Item label={t('title', 'Title')}>
                                            {pi.metadata.title}
                                        </Descriptions.Item>
                                    )}
                                    {pi.metadata?.byline && (
                                        <Descriptions.Item label={t('asset-byline', 'Asset Byline')}>
                                            {pi.metadata.byline}
                                        </Descriptions.Item>
                                    )}
                                    {pi.metadata?.headline && (
                                        <Descriptions.Item label={t('headline', 'Headline')}>
                                            {pi.metadata.headline}
                                        </Descriptions.Item>
                                    )}
                                    {pi.metadata?.extended_description && (
                                        <Descriptions.Item label={t('extended-description', 'Extended Description')}>
                                            {pi.metadata.extended_description}
                                        </Descriptions.Item>
                                    )}
                                    {pi.metadata?.keywords && (
                                        <Descriptions.Item label={t('keywords', 'Keywords')}>
                                            {pi.metadata.keywords}
                                        </Descriptions.Item>
                                    )}
                                    {pi.metadata?.usage_rights && (
                                        <Descriptions.Item label={t('usage-rights', 'Usage Rights')}>
                                            {pi.metadata.usage_rights}
                                        </Descriptions.Item>
                                    )}
                                    {pi.credit_line && (
                                        <Descriptions.Item label={t('credit-line', 'Credit Line')}>
                                            {pi.credit_line}
                                        </Descriptions.Item>
                                    )}
                                    {pi.published_asset.metadata?.categories && pi.published_asset.metadata.categories.length > 0 && (
                                        <Descriptions.Item label={t('categories', 'Categories')}>
                                            <Space size={[0, 4]} wrap>
                                                {pi.published_asset.metadata.categories.map((cat, idx) => (
                                                    <Tag key={idx}>{cat}</Tag>
                                                ))}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {pi.published_asset.metadata?.tags && pi.published_asset.metadata.tags.length > 0 && (
                                        <Descriptions.Item label={t('tags', 'Tags')}>
                                            <Space size={[0, 4]} wrap>
                                                {pi.published_asset.metadata.tags.map((tag, idx) => (
                                                    <Tag key={idx} color="blue">{tag}</Tag>
                                                ))}
                                            </Space>
                                        </Descriptions.Item>
                                    )}
                                    {pi.published_asset.abstract && (
                                        <Descriptions.Item label={t('abstract', 'Abstract')}>
                                            {pi.published_asset.abstract}
                                        </Descriptions.Item>
                                    )}
                                </Descriptions>
                            </Card>
                        </List.Item>
                    )}
                />
            )}

            {asset.editable && (
                <Collapse ghost>
                    <Collapse.Panel
                        key="add-publication"
                        header={<><PlusOutlined /> {t('add-publication', 'Add Publication')}</>}
                    >
                        <Formik
                            initialValues={{ filename: asset.filename || '' }}
                            enableReinitialize
                            onSubmit={handleCreate}
                        >
                            {({ submitForm, isSubmitting }) => (
                                <Form>
                                    <Descriptions bordered size="small" column={1}
                                        title={t('publication-details', 'Publication Details')}
                                    >
                                        <Descriptions.Item label={<>{t('publisher-uid', 'Publisher UID')} <Typography.Text type="danger">*</Typography.Text></>}>
                                            <Input name="publisher_uid" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<>{t('publisher-name', 'Publisher Name')} <Typography.Text type="danger">*</Typography.Text></>}>
                                            <Input name="publisher_name" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={<>{t('publication-name', 'Publication Name')} <Typography.Text type="danger">*</Typography.Text></>}>
                                            <Input name="publication_name" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('edition', 'Edition')}>
                                            <Input name="edition" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('article-uid', 'Article UID')}>
                                            <Input name="article_uid" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('article-name', 'Article Name')}>
                                            <Input name="article_name" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('headline', 'Headline')}>
                                            <Input name="headline" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('subhead', 'Subhead')}>
                                            <Input name="subhead" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('article-byline', 'Byline')}>
                                            <Input name="byline" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('publication-date', 'Publication Date')}>
                                            <DatePicker name="publication_date" style={{ width: '100%' }} />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('abstract', 'Abstract')}>
                                            <Input.TextArea name="abstract" rows={2} />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('publication-urls', 'Publication URLs')}>
                                            <FormikSelect mode="tags" name="publication_urls" style={{ width: '100%' }} placeholder={t('type-url-press-enter', 'Type URL and press Enter')} />
                                        </Descriptions.Item>
                                    </Descriptions>

                                    <Descriptions bordered size="small" column={1} style={{ marginTop: 16 }}
                                        title={t('image-details', 'Image Details')}
                                    >
                                        <Descriptions.Item label={<>{t('filename', 'Filename')} <Typography.Text type="danger">*</Typography.Text></>}>
                                            <Input name="filename" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('url', 'URL')}>
                                            <Input name="url" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('cdn-link', 'CDN Link')}>
                                            <Input name="cdn_link" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('usage-type', 'Usage Type')}>
                                            <Input name="usage_type" placeholder="e.g. featured_image, inline_image" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('credit-line', 'Credit Line')}>
                                            <Input name="credit_line" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('caption', 'Caption')}>
                                            <Input.TextArea name="caption" rows={2} />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('alt-text', 'Alt Text')}>
                                            <Input name="alt_text" />
                                        </Descriptions.Item>
                                        <Descriptions.Item label={t('restrictions', 'Restrictions')}>
                                            <Input name="restrictions" />
                                        </Descriptions.Item>
                                        <Descriptions.Item key="_submit">
                                            <Button icon={<CheckOutlined />} onClick={submitForm} loading={isSubmitting} block type="primary">
                                                {t('button-save-publication', 'Save Publication')}
                                            </Button>
                                        </Descriptions.Item>
                                    </Descriptions>
                                </Form>
                            )}
                        </Formik>
                    </Collapse.Panel>
                </Collapse>
            )}
        </>
    );
};

export default PublishedImages;
